import React, { useState, useEffect } from "react";
import { db } from "./firebase";
import { collection, getDocs, query, orderBy, limit, doc, updateDoc } from "firebase/firestore";
import axios from "axios";
import "./AdminOrders.css";

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingManifestId, setGeneratingManifestId] = useState(null);
  const [updatingOrderId, setUpdatingOrderId] = useState(null);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const q = query(
          collection(db, "orders"),
          orderBy("createdAt", "desc"),
          limit(50)
        );
        const querySnapshot = await getDocs(q);
        const ordersList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setOrders(ordersList);
      } catch (error) {
        console.error("Error fetching orders:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const handleGenerateManifest = async (shipmentId, orderId) => {
    if (!shipmentId) {
      alert("No shipment ID found for this order. It may not be processed by Shiprocket yet.");
      return;
    }

    setGeneratingManifestId(shipmentId);
    try {
      const response = await axios.post("https://us-central1-nabi-1708b.cloudfunctions.net/generateShiprocketManifest", {
        shipmentId: shipmentId,
        orderId: orderId
      });

      if (response.data && response.data.success) {
        alert("Manifest generated successfully! The PDF will now open.");
        window.open(response.data.manifest_url, "_blank");
      } else {
        alert("Failed to generate manifest. " + (response.data.error || ""));
      }
    } catch (error) {
      console.error("Manifest generation error:", error);
      const details = error.response?.data?.details;
      const detailsStr = details ? JSON.stringify(details) : "";
      alert("Error generating manifest: " + (error.response?.data?.error || error.message) + "\n" + detailsStr);
    } finally {
      setGeneratingManifestId(null);
    }
  };

  const handleToggleComplete = async (orderId, currentStatus) => {
    setUpdatingOrderId(orderId);
    try {
      const orderRef = doc(db, "orders", orderId);
      await updateDoc(orderRef, {
        adminCompleted: !currentStatus
      });
      // Update local state to reflect change immediately
      setOrders(orders.map(o => o.id === orderId ? { ...o, adminCompleted: !currentStatus } : o));
    } catch (error) {
      console.error("Error updating completion status:", error);
      alert("Failed to update status");
    } finally {
      setUpdatingOrderId(null);
    }
  };

  if (loading) {
    return <div style={{ padding: "40px", textAlign: "center" }}>Loading orders...</div>;
  }

  return (
    <div className="admin-orders-container">
      <h1 className="admin-orders-title">Shiprocket Orders (Admin)</h1>
      <p className="admin-orders-subtitle">
        Manage your recent store orders. Print labels to paste on packages, and generate manifests to hand over to the courier.
      </p>

      {orders.length === 0 ? (
        <p>No orders found.</p>
      ) : (
        <div className="admin-table-wrapper">
          <table className="admin-orders-table">
            <thead>
              <tr>
                <th className="admin-orders-th">Done</th>
                <th className="admin-orders-th">Order ID</th>
                <th className="admin-orders-th">Customer</th>
                <th className="admin-orders-th">Items</th>
                <th className="admin-orders-th">Status</th>
                <th className="admin-orders-th">Tracking (AWB)</th>
                <th className="admin-orders-th">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => {
                const customerName = order.customerName || order.customer?.name || "N/A";
                const totalItems = order.items ? order.items.reduce((sum, item) => sum + (item.qty || 1), 0) : 0;
                
                return (
                  <tr key={order.id} style={{ opacity: order.adminCompleted ? 0.6 : 1 }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = order.adminCompleted ? "transparent" : "#fcfcfc"} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}>
                    <td className="admin-orders-td" data-label="Done" style={{ textAlign: "center" }}>
                      <input 
                        type="checkbox" 
                        checked={!!order.adminCompleted} 
                        onChange={() => handleToggleComplete(order.id, order.adminCompleted)}
                        disabled={updatingOrderId === order.id}
                        style={{ width: "20px", height: "20px", cursor: "pointer", accentColor: "#003049" }}
                      />
                    </td>
                    <td className="admin-orders-td" data-label="Order ID">
                      <span style={{ fontWeight: "500", fontSize: "0.9rem", textDecoration: order.adminCompleted ? "line-through" : "none" }}>{order.id}</span>
                      <br/>
                      <span style={{ fontSize: "0.8rem", color: "#888" }}>
                        {order.createdAt ? new Date(order.createdAt.seconds * 1000).toLocaleDateString() : "Unknown Date"}
                      </span>
                    </td>
                    <td className="admin-orders-td" data-label="Customer">
                      {customerName}
                      <br/>
                      <span style={{ fontSize: "0.8rem", color: "#888" }}>{order.email || order.customer?.email}</span>
                    </td>
                    <td className="admin-orders-td" data-label="Items">{totalItems} items</td>
                    <td className="admin-orders-td" data-label="Status">
                      <span className="admin-status-badge" style={{
                        backgroundColor: order.shipmentStatus?.toLowerCase() === "processed" ? "#e8f5e9" : "#fff3e0",
                        color: order.shipmentStatus?.toLowerCase() === "processed" ? "#2e7d32" : "#e65100"
                      }}>
                        {order.shipmentStatus || "Pending"}
                      </span>
                    </td>
                    <td className="admin-orders-td" data-label="Tracking (AWB)">
                      {order.awbCode ? (
                        <span style={{ fontFamily: "monospace", backgroundColor: "#f0f0f0", padding: "2px 6px", borderRadius: "4px" }}>
                          {order.awbCode}
                        </span>
                      ) : (
                        <span style={{ color: "#aaa" }}>Not Assigned</span>
                      )}
                    </td>
                    <td className="admin-orders-td" data-label="Actions">
                      <div className="admin-actions-cell">
                        {order.labelUrl ? (
                          <button 
                            className="admin-btn-primary"
                            onClick={() => window.open(order.labelUrl, "_blank")}
                          >
                            Print Label
                          </button>
                        ) : (
                          <button className="admin-btn-primary" disabled>
                            Label N/A
                          </button>
                        )}
                        
                        <button 
                          className="admin-btn-secondary"
                          onClick={() => handleGenerateManifest(order.shiprocketShipmentId, order.shiprocketOrderId || order.id)}
                          disabled={generatingManifestId === order.shiprocketShipmentId}
                        >
                          {generatingManifestId === order.shiprocketShipmentId ? "Processing..." : "Get Manifest"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
